// hotkeys/HotkeysHelp.tsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useHotkeys } from "./HotkeysProvider";
import { HotkeysSuppressor } from "./HotkeysSuppressor";
import {
  CALENDAR_KEYBOARD_SPEC,
  CalendarKeyboardActionId,
} from "../molecules/Calendar/hotkeys/keyboardSpec";

/**
 * Calendar ("termin" scope) keys handled by raw document listeners
 * (CellNavigation.ts / EventShelf.tsx / useGridKeyboard.ts), NOT registered
 * with the provider — so `list()` never sees them. Pulled from the keyboard
 * spec so this overlay stays complete without duplicating descriptions.
 */
const RAW_TERMIN_SPEC_ACTIONS: CalendarKeyboardActionId[] = [
  "cellMove",
  "cellJump",
  "escapeStagedClear",
];

interface HotkeysHelpProps {
  open: boolean;
  onClose: () => void;
}

export const HotkeysHelp: React.FC<HotkeysHelpProps> = ({ open, onClose }) => {
  const { list, activeScope } = useHotkeys();

  const hotkeys = list();

  // The Scheduler keeps several calendar variants mounted (hidden via
  // display:none), each registering identical guarded bindings — collapse
  // those exact copies so every shortcut is listed once.
  const seen = new Set<string>();
  const dedupedHotkeys = hotkeys.filter((hotkey) => {
    const key = `${hotkey.scope ?? "global"}|${hotkey.combo}|${hotkey.description ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Group hotkeys by scope
  const groupedHotkeys = dedupedHotkeys.reduce((acc, hotkey) => {
    const scope = hotkey.scope || "global";
    if (!acc[scope]) acc[scope] = [];
    acc[scope].push(hotkey);
    return acc;
  }, {} as Record<string, typeof hotkeys>);

  // Sort hotkeys within each scope by combo length then alphabetically
  Object.keys(groupedHotkeys).forEach(scope => {
    groupedHotkeys[scope].sort((a, b) => {
      const aLen = a.combo.split(" ").length;
      const bLen = b.combo.split(" ").length;
      if (aLen !== bLen) return aLen - bLen;
      return a.combo.localeCompare(b.combo);
    });
  });

  // Active scope first, then global, then the rest alphabetically.
  const orderedScopes = Object.keys(groupedHotkeys).sort((a, b) => {
    const rank = (s: string) => (s === activeScope ? 0 : s === "global" ? 1 : 2);
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  const formatCombo = (combo: string) => {
    return combo.split(" ").map((key, index) => {
      // Uppercase letters are registered for Shift+letter presses — label them
      // explicitly, otherwise "j" and "J" would render identically.
      const isShifted = key.length === 1 && key !== key.toLowerCase();
      const label = key === " " ? "Space" : isShifted ? `Shift + ${key}` : key.toUpperCase();
      return (
        <React.Fragment key={index}>
          {index > 0 && <span style={{ margin: "0 4px", color: "#666" }}>→</span>}
          <Chip
            size="small"
            label={label}
            variant="outlined"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              height: "20px",
              "& .MuiChip-label": { px: 1 }
            }}
          />
        </React.Fragment>
      );
    });
  };

  const getScopeDisplayName = (scope: string) => {
    const names: Record<string, string> = {
      global: "Global",
      dashboard: "Dashboard",
      patient: "Patienten",
      receipt: "Rezepte",
      terminplan: "Terminplan",
      transactions: "Transaktionen",
      admin: "Administration",
      termin: "Terminkalender",
      continuous: "Fortlaufende Planung",
      history: "Historie",
      biDashboard: "BI-Dashboard",
      sales: "Sales",
      "doctor-management": "Ärzteverwaltung",
      login: "Login"
    };
    return names[scope] || scope;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "60vh" }
      }}
    >
      <HotkeysSuppressor>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Tastaturkürzel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {/* No colour overrides needed: the theme's white chip background is
                scoped to chips without an explicit colour, so color="primary"
                keeps MUI's own palette background and contrastText label. */}
            Aktueller Bereich: <Chip size="small" label={getScopeDisplayName(activeScope)} color="primary" />
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {orderedScopes.length === 0 ? (
            <Typography color="text.secondary">
              Keine Tastaturkürzel registriert.
            </Typography>
          ) : (
            <Box>
              {orderedScopes.map((scope) => {
                const scopeHotkeys = groupedHotkeys[scope];
                return (
                <Box key={scope} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {getScopeDisplayName(scope)}
                    {scope === activeScope && (
                      <Chip
                        size="small"
                        label="Aktiv"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>Kürzel</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Funktion</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scope === "termin" &&
                          RAW_TERMIN_SPEC_ACTIONS.map((actionId) => {
                            const spec = CALENDAR_KEYBOARD_SPEC[actionId];
                            return (
                              <TableRow key={`spec-${actionId}`}>
                                <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                                  <Chip
                                    size="small"
                                    label={spec.keys}
                                    variant="outlined"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.75rem",
                                      height: "20px",
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{spec.description}</TableCell>
                              </TableRow>
                            );
                          })}
                        {scopeHotkeys.map((hotkey) => (
                          <TableRow key={hotkey.id}>
                            <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                              {formatCombo(hotkey.combo)}
                            </TableCell>
                            <TableCell>
                              {hotkey.description || <em>Keine Beschreibung</em>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                );
              })}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary">
                <strong>Hinweise:</strong>
                <br />
                • Einzelne Tasten funktionieren nur außerhalb von Eingabefeldern
                <br />
                • Der Pfeil (→) kennzeichnet Tastenfolgen – Tasten nacheinander mit kurzer Pause drücken
                <br />
                • Kürzel gelten jeweils für den aktuellen Bereich der Anwendung
                <br />
                • <Chip size="small" label="?" variant="outlined" /> öffnet bzw. schließt diese Übersicht
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </HotkeysSuppressor>
    </Dialog>
  );
};